import React from 'react';
import {
  Stethoscope,
  DollarSign,
  TrendingUp,
  BarChart3,
  Calendar,
  Layers
} from 'lucide-react';
import { DashboardOverviewResponse, SpecialtyAnalysis } from '../types/dashboard';
import { formatCurrency, formatNumber } from '../utils/formatters';

interface ClinicalOverviewProps {
  overview: DashboardOverviewResponse | null;
  specialties: SpecialtyAnalysis[];
}

const ClinicalOverview: React.FC<ClinicalOverviewProps> = ({ overview, specialties }) => {
  if (!overview) {
    return (
      <div className="glass-card p-12 rounded-2xl border border-border text-center">
        <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
        <h3 className="text-base font-bold text-foreground">No Clinical Records Found</h3>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
          Upload medical claims data to view specialty distributions and clinical volume metrics.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="glass-panel p-6 rounded-2xl border border-border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-teal-600 text-white flex items-center justify-center shadow-md shadow-primary-500/20">
            <Stethoscope className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Clinical & Specialty Intelligence</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Summary of claims data volume, clinical specialties, and financial throughput
            </p>
          </div>
        </div>

        {overview.dateRange?.start && overview.dateRange?.end && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-muted border border-border text-xs font-semibold text-muted-foreground">
            <Calendar className="w-3.5 h-3.5 text-primary-600" />
            <span>Audit Period:</span>
            <span className="font-mono text-foreground">{overview.dateRange.start} → {overview.dateRange.end}</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Total Claims</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-foreground">{formatNumber(overview.totalClaims)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Processed claims</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Total Invoiced</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-foreground">{formatCurrency(overview.totalAmount)}</p>
          <p className="text-[11px] text-emerald-600 font-semibold mt-1">Gross medical billing</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Average Claim</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-foreground">{formatCurrency(overview.avgAmount)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Per individual encounter</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Top Specialty</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100">
              <BarChart3 className="w-4 h-4" />
            </div>
          </div>
          <p className="text-base font-bold text-foreground truncate" title={overview.topSpecialty?.name ?? '—'}>
            {overview.topSpecialty?.name ?? '—'}
          </p>
          <p className="text-[11px] text-amber-700 font-mono font-bold mt-1">
            {formatNumber(overview.topSpecialty?.claimCount ?? 0)} claims
          </p>
        </div>
      </div>

      {/* Specialties Data Table */}
      {specialties && specialties.length > 0 && (
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-primary-600" />
              <h3 className="text-xs font-bold text-foreground">Top Clinical Specialties by Volume</h3>
            </div>
            <span className="text-xs text-muted-foreground font-semibold">
              Showing top {Math.min(8, specialties.length)} specialties
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted/60 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                  <th className="py-3 px-4">Specialty</th>
                  <th className="py-3 px-4 text-right">Claims Count</th>
                  <th className="py-3 px-4 text-right">Total Invoiced</th>
                  <th className="py-3 px-4 text-right">Avg / Claim</th>
                  <th className="py-3 px-4 text-right">Volume Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-xs">
                {specialties.slice(0, 8).map((specialty, idx) => {
                  const pct = specialty?.percentage ?? 0;
                  return (
                    <tr key={idx} className="hover:bg-muted/40 transition">
                      <td className="py-3.5 px-4 font-bold text-foreground">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-md bg-muted text-muted-foreground flex items-center justify-center text-[10px] font-mono">
                            {idx + 1}
                          </span>
                          <span>{specialty?.name ?? '—'}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-right font-mono font-bold text-foreground">
                        {formatNumber(specialty?.claims ?? 0)}
                      </td>
                      <td className="py-3.5 px-4 text-right font-mono font-bold text-emerald-600">
                        {formatCurrency(specialty?.totalAmount ?? 0)}
                      </td>
                      <td className="py-3.5 px-4 text-right font-mono text-muted-foreground">
                        {formatCurrency(specialty?.avgAmount ?? 0)}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <div className="w-20 bg-muted h-2 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-600 rounded-full"
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <span className="font-mono font-bold text-xs min-w-[36px] text-foreground">
                            {pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicalOverview;